/**
 * Hook wrapper around the pipeline activity-log drawer reactive variable
 * (SE-5504). Mirrors `useReviewQueueDrawerState`.
 * @module features/player-kanban/hooks/usePipelineActivityLogDrawerState
 */

import { useReactiveVar } from "@apollo/client";
import { useCallback, useMemo } from "react";

import {
  type PipelineActivityLogDrawerState,
  pipelineActivityLogDrawerVar,
  resetPipelineActivityLogDrawer,
} from "../stores/pipelineActivityLogDrawerStore";

/** Public API returned by {@link usePipelineActivityLogDrawerState}. */
export interface UsePipelineActivityLogDrawerStateResult {
  /** Current drawer state. */
  readonly state: PipelineActivityLogDrawerState;
  /**
   * Open the log for one pipeline.
   * @param kanbanBoardId - The pipeline to show.
   */
  readonly open: (kanbanBoardId?: string | null) => void;
  /** Close the drawer. */
  readonly close: () => void;
}

/**
 * Subscribe to the activity-log drawer variable + open/close helpers.
 * @returns The {@link UsePipelineActivityLogDrawerStateResult}.
 */
export function usePipelineActivityLogDrawerState(): UsePipelineActivityLogDrawerStateResult {
  const state = useReactiveVar(pipelineActivityLogDrawerVar);

  const open = useCallback(
    (kanbanBoardId: string | null = null) =>
      pipelineActivityLogDrawerVar({ open: true, kanbanBoardId }),
    []
  );

  const close = useCallback(() => resetPipelineActivityLogDrawer(), []);

  return useMemo(() => ({ state, open, close }), [state, open, close]);
}
